import React, { useCallback, useState } from 'react';
import { AImage } from '../../atoms/AImage/index';
import { MTag, MTagType } from '../../molecules/MTag/index';
import { MCarouselControls, MCarouselControlsType } from '../../molecules/MCarouselControls/index';
import { MMediaCarouselProps, MMediaCarouselCarouselStyle, MMediaImageMultiItem } from './MMediaCarousel.types';
import * as Styled from './MMediaCarousel.styles';

const COMPONENT_NAME = 'm-web-ui-kit-MMediaCarousel';
const TEST_ID = 'web-ui-kit-MMediaCarousel';

const getImageSrc = (item: MMediaImageMultiItem): string | undefined => {
  const assetPath = item.aImage?.image?.assetPath;
  const damPath = item.aImage?.image?.damPath;
  return assetPath || damPath || undefined;
};

const getImageAlt = (item: MMediaImageMultiItem): string => {
  return item.aImage?.image?.altText || '';
};

export const MMediaCarousel: React.FC<MMediaCarouselProps> = ({
  carouselStyle = MMediaCarouselCarouselStyle.Standard,
  aImage = [],
  mMediaImageMulti = [],
  className,
  ...rest
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = mMediaImageMulti.length > 0 ? mMediaImageMulti : [];
  const hasSlides = slides.length > 0;

  const totalSlides = hasSlides ? slides.length : aImage.length;

  const handleNext = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const handlePrev = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  if (!hasSlides && aImage.length === 0) {
    return null;
  }

  if (hasSlides) {
    const currentItem = slides[currentSlide];
    const imageSrc = getImageSrc(currentItem);
    const imageAlt = getImageAlt(currentItem);
    const showTag = currentItem?.tag === 'true' && currentItem?.mTag?.aLabel?.text;

    return (
      <Styled.Wrapper
        carouselStyle={carouselStyle}
        hover={false}
        className={className}
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        {...rest}
      >
        <Styled.CarouselContainer>
          {imageSrc && (
            <>
              <Styled.SlideContent>
                <AImage src={imageSrc} alt={imageAlt} loading="eager" />
              </Styled.SlideContent>
              {showTag && currentItem.mTag && (
                <Styled.TagWrapper>
                  <MTag aLabel={{ text: currentItem.mTag.aLabel?.text || '' }} type={MTagType.Overlay} />
                </Styled.TagWrapper>
              )}
              <Styled.ControlsWrapper>
                <MCarouselControls
                  type={MCarouselControlsType.DotsWithOverlay}
                  imageOverlay={true}
                  totalSlides={totalSlides}
                  currentSlide={currentSlide}
                  onNext={handleNext}
                  onPrev={handlePrev}
                />
              </Styled.ControlsWrapper>
            </>
          )}
        </Styled.CarouselContainer>
      </Styled.Wrapper>
    );
  }

  return (
    <Styled.Wrapper
      carouselStyle={carouselStyle}
      hover={false}
      className={className}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      {...rest}
    >
      <Styled.CarouselContainer>
        {aImage[currentSlide] && (
          <Styled.SlideContent>
            <AImage src={aImage[currentSlide].src} alt={aImage[currentSlide].alt} loading="eager" />
          </Styled.SlideContent>
        )}
        <Styled.ControlsWrapper>
          <MCarouselControls
            type={MCarouselControlsType.DotsWithOverlay}
            imageOverlay={true}
            totalSlides={totalSlides}
            currentSlide={currentSlide}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        </Styled.ControlsWrapper>
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};

export default MMediaCarousel;
export { MMediaCarouselCarouselStyle } from './MMediaCarousel.types';
export type { MMediaCarouselProps, MMediaImageMultiItem } from './MMediaCarousel.types';
